import { useEffect, useRef, useState, type ComponentType } from 'react';
import { useTheme } from '../hooks/useTheme';
import {
  geographicLineToPath,
  geographicRingsToPath,
  projectOnEuropeanGlobe,
} from '../utils/coverageGlobe';
import {
  LANDING_COVERAGE_COUNTRIES,
  LANDING_COVERAGE_ROUTES,
} from '../data/landingCoverageCountries';

interface LandingCoverageGlobeProps {
  isVisible: boolean;
  label: string;
}

interface CoverageGlobeCanvasProps {
  isActive: boolean;
  reducedMotion: boolean;
  theme: 'light' | 'dark';
}

const PARALLELS = [-30, 0, 30, 60].map((latitude) => geographicLineToPath(
  Array.from({ length: 73 }, (_, index): [number, number] => [-180 + index * 5, latitude]),
));
const MERIDIANS = Array.from({ length: 12 }, (_, index) => -165 + index * 30).map((longitude) => geographicLineToPath(
  Array.from({ length: 45 }, (_, index): [number, number] => [longitude, -88 + index * 4]),
));

function supportsWebGl(): boolean {
  if (typeof document === 'undefined' || typeof WebGLRenderingContext === 'undefined') {
    return false;
  }

  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('webgl2') || canvas.getContext('webgl');
    context?.getExtension('WEBGL_lose_context')?.loseContext();
    return Boolean(context);
  } catch {
    return false;
  }
}

function useReducedMotion(): boolean {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setReducedMotion(query.matches);
    updatePreference();
    query.addEventListener('change', updatePreference);
    return () => query.removeEventListener('change', updatePreference);
  }, []);

  return reducedMotion;
}

function StaticCoverageGlobe({ label }: Pick<LandingCoverageGlobeProps, 'label'>) {
  const projectedCountries = LANDING_COVERAGE_COUNTRIES.map((country) => ({
    ...country,
    point: projectOnEuropeanGlobe(country.latitude, country.longitude),
  }));

  return (
    <svg
      viewBox="40 10 200 200"
      className="absolute inset-0 h-full w-full transition-opacity duration-500 motion-reduce:transition-none"
      role="img"
      aria-label={label}
      focusable="false"
      data-coverage-map="europe"
    >
      <title>{label}</title>
      <defs>
        <radialGradient id="coverage-globe-surface" cx="35%" cy="28%" r="72%">
          <stop offset="0%" className="theme-coverage-globe-highlight" />
          <stop offset="100%" className="theme-coverage-globe-shadow" />
        </radialGradient>
        <clipPath id="coverage-globe-clip">
          <circle cx="140" cy="110" r="94" />
        </clipPath>
      </defs>
      <circle className="theme-coverage-globe-halo" cx="140" cy="110" r="98" />
      <circle cx="140" cy="110" r="94" fill="url(#coverage-globe-surface)" />
      <g className="theme-coverage-globe-grid" clipPath="url(#coverage-globe-clip)" fill="none" strokeWidth="0.55">
        {PARALLELS.map((path) => <path key={path} d={path} />)}
        {MERIDIANS.map((path) => <path key={path} d={path} />)}
      </g>
      <g clipPath="url(#coverage-globe-clip)" fill="none" strokeWidth="0.7">
        {LANDING_COVERAGE_COUNTRIES.filter((country) => country.svgRings?.length).map((country) => (
          <path
            className={`theme-coverage-globe-country ${country.status === 'pending' ? 'theme-coverage-globe-country-pending' : ''}`}
            key={country.code}
            data-country-geometry={country.code}
            d={geographicRingsToPath(country.svgRings ?? [])}
          />
        ))}
      </g>
      {LANDING_COVERAGE_ROUTES.map((route) => {
        const from = projectedCountries.find((country) => country.code === route.from);
        const to = projectedCountries.find((country) => country.code === route.to);
        if (!from || !to) return null;
        const isPending = from.status === 'pending' || to.status === 'pending';
        const routeControlX = (from.point.x + to.point.x) / 2;
        const routeControlY = Math.min(from.point.y, to.point.y) - 6;
        return (
          <path
            key={`${from.code}-${to.code}`}
            className={`theme-coverage-globe-route ${isPending ? 'theme-coverage-globe-route-pending' : ''}`}
            data-country-route={`${from.code}-${to.code}`}
            d={`M${from.point.x} ${from.point.y} Q${routeControlX} ${routeControlY} ${to.point.x} ${to.point.y}`}
            fill="none"
            strokeWidth="1.8"
          />
        );
      })}
      {projectedCountries.map((country) => (
        <g
          key={country.code}
          data-country={country.code}
          data-latitude={country.latitude}
          data-longitude={country.longitude}
          data-status={country.status}
        >
          <circle className={`theme-coverage-globe-marker-glow ${country.status === 'pending' ? 'theme-coverage-globe-marker-glow-pending' : ''}`} cx={country.point.x} cy={country.point.y} r="5" />
          <circle className={`theme-coverage-globe-marker ${country.status === 'pending' ? 'theme-coverage-globe-marker-pending' : ''}`} cx={country.point.x} cy={country.point.y} r="2.7" />
        </g>
      ))}
    </svg>
  );
}

export function LandingCoverageGlobe({ isVisible, label }: LandingCoverageGlobeProps) {
  const { theme } = useTheme();
  const reducedMotion = useReducedMotion();
  const shellRef = useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = useState(false);
  const [CanvasGlobe, setCanvasGlobe] = useState<ComponentType<CoverageGlobeCanvasProps> | null>(null);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell || typeof IntersectionObserver === 'undefined') {
      setIsActive(isVisible);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsActive(entry.isIntersecting),
      { rootMargin: '160px 0px', threshold: 0.01 },
    );
    observer.observe(shell);
    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isActive || CanvasGlobe || !supportsWebGl()) return undefined;
    let cancelled = false;

    void import('./LandingCoverageGlobeCanvas')
      .then(({ LandingCoverageGlobeCanvas }) => {
        if (!cancelled) setCanvasGlobe(() => LandingCoverageGlobeCanvas);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [CanvasGlobe, isActive]);

  return (
    <div
      ref={shellRef}
      className="theme-coverage-globe-shell relative mx-auto aspect-square w-[62%] max-w-[14rem] select-none isolate sm:w-full sm:max-w-[25rem]"
    >
      <StaticCoverageGlobe label={label} />
      {CanvasGlobe && (
        <div
          className="absolute inset-0 z-10 cursor-grab overflow-hidden rounded-full animate-app-fade-in active:cursor-grabbing"
          aria-hidden="true"
          data-coverage-globe="webgl"
        >
          <CanvasGlobe isActive={isActive} reducedMotion={reducedMotion} theme={theme} />
        </div>
      )}
    </div>
  );
}
